// The invite/edit FORM rules, pure (ORP-18889). Everything `UserPermissionModal.vue` decided
// inline — chip classification (`:407-413`), payload assembly (`:479-492`), the five swal
// validations (`:457-478`) and the dead `isFormUnchanged` (`:316-344`) — lives here, so the SFC is
// layout and the rules are unit-testable without mounting anything.
//
// Three legacy defects are fixed by MOVING code rather than rewriting it:
//
//   * `setUser()` VALIDATED AND MUTATED at once (`:462-468`): at dashboard scope with no role it
//     assigned `roles.find(r => r.value === 'viewer') || roles[0]`. That `|| roles[0]` is a
//     POSITIONAL fallback — the same defect class `memberCatalog.ts` was built to kill, one role
//     insertion away from silently granting `admin`. Defaulting now happens on the SCOPE CHANGE
//     (`roleForScope`), addressed by name, and validation is side-effect free.
//   * `formData.isInvite` / `formData.obj` were never declared in the `reactive()` initialiser
//     (`:291-298`) yet read in six places — `isInvite` is plain `undefined` on the create path.
//     Here the edit target is an explicit `MemberListRow | null` and the discriminated union
//     replaces the flag entirely.
//   * `isFormUnchanged` was computed and never referenced (D9), so a no-op Update still POSTed.
//     `isGrantUnchanged` is the same rule, and `MemberInviteModal` actually disables on it.
import {
  isPendingInviteRow,
  type AssistantSeats,
  type MemberListRow,
  type MemberRole,
  type MemberScope,
} from '../models/member.types'
import { assignableRoles } from '../models/memberCatalog'
import type {
  BulkInvitePayload,
  MemberGrantDraft,
  MemberGrantPayload,
} from '../models/memberInvite.types'

import { foldEmail } from './memberList'

import type { UiTone } from '@/shared/ui'

/** v3's `EMAIL_RE` (`:396`) verbatim — deliberately not "improved": widening it here would admit
 *  addresses the backend then rejects, turning a local hint into a round-trip failure. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/

/** v3's four chip types (`:401-405`). ADMIT-then-mark: a bad address stays visible and tinted. */
export type InviteEmailState = 'valid' | 'self' | 'existing' | 'invalid'

export interface InviteEmailContext {
  /** The signed-in user's address. You cannot invite yourself (`:409`). */
  selfEmail?: string | undefined
  /** Every address already on the account — `useMembers().existingEmails`, already folded. */
  existingEmails: ReadonlySet<string>
  /**
   * Addresses exempt from the `existing` check — see `MemberInviteModal`'s retry decision.
   *
   * v3's `retryFailed()` (`:370-375`) re-typed each retried address through `getChipType`, and by
   * then the parent's `existingEmails` had been refreshed by the intervening member re-read. An
   * address the backend reported as `error` could therefore come back typed `existing`, drop out
   * of `validChips` and become SILENTLY UN-RETRYABLE — the Retry button put it on screen and the
   * Invite button then refused to send it, with no message. A retried address is by definition one
   * the backend did NOT grant, so exempting it from the duplicate check is not a loosening.
   */
  retryable?: ReadonlySet<string>
}

export function classifyInviteEmail(email: string, context: InviteEmailContext): InviteEmailState {
  const folded = foldEmail(email)
  if (context.selfEmail !== undefined && folded === foldEmail(context.selfEmail)) return 'self'
  if (!EMAIL_PATTERN.test(folded)) return 'invalid'
  if (context.retryable?.has(folded) === true) return 'valid'
  return context.existingEmails.has(folded) ? 'existing' : 'valid'
}

/** v3 tinted `self` violet and both blocking states red (`:40-48`); `existing` and `invalid` are
 *  different problems with different fixes, so they separate here. */
const STATE_TONE: Readonly<Record<InviteEmailState, UiTone>> = {
  valid: 'neutral',
  self: 'info',
  existing: 'warning',
  invalid: 'danger',
}

export function inviteEmailTone(state: InviteEmailState): UiTone {
  return STATE_TONE[state]
}

/** The addresses that will actually be sent — v3's `validChips` (`:401`). */
export function sendableEmails(emails: readonly string[], context: InviteEmailContext): string[] {
  return emails.filter((email) => classifyInviteEmail(email, context) === 'valid')
}

/* ── Role defaulting, by name ────────────────────────────────────────────────── */

/**
 * The role to hold after a scope change. A role that is still assignable survives; otherwise v3's
 * dashboard default (`viewer`) applies and every other scope falls back to "choose one".
 * `assignableRoles` is the catalog's, so the matrix is never re-derived here.
 */
export function roleForScope(current: MemberRole | null, scope: MemberScope): MemberRole | null {
  const allowed = assignableRoles(scope)
  if (current !== null && allowed.some((entry) => entry.role === current)) return current
  return scope === 'dashboard' ? 'viewer' : null
}

/* ── Seeding an edit ─────────────────────────────────────────────────────────── */

/**
 * v3's `updateUserOpen()` (`:562-578`), minus the two undeclared fields and the `formModal` write.
 * `offerAssistant` is whether the form shows the MCP switch; when it does not, the draft carries
 * `null` and the edit leaves the stored answer alone.
 */
export function draftFromRow(row: MemberListRow, offerAssistant = false): MemberGrantDraft {
  return {
    scope: row.scope,
    role: row.role,
    workspaceIds: row.workspaces.map((workspace) => workspace.id),
    dashboardIds: row.dashboards.map((dashboard) => dashboard.id),
    mcpEnabled: offerAssistant ? row.mcpEnabled : null,
  }
}

export function emptyDraft(offerAssistant = false): MemberGrantDraft {
  return {
    scope: 'account',
    role: null,
    workspaceIds: [],
    dashboardIds: [],
    mcpEnabled: offerAssistant ? false : null,
  }
}

/** The form's starting point on every open: the edited row, or a blank invite. */
export function seedDraft(row: MemberListRow | null, offerAssistant: boolean): MemberGrantDraft {
  return row ? draftFromRow(row, offerAssistant) : emptyDraft(offerAssistant)
}

/* ── The AI-assistant (MCP) switch ───────────────────────────────────────────── */

/** The backend grants the assistant at account or workspace scope only, and REFUSES a `true` on a
 *  dashboard grant (`accounts/views.py` — "AI assistant access can only be granted…"). */
export function assistantAllowed(scope: MemberScope): boolean {
  return scope !== 'dashboard'
}

/** Seats the submit would newly occupy. Someone who already holds the permission spends nothing,
 *  and every invited address spends one (the backend counts them per address). */
function seatsNeeded(
  draft: MemberGrantDraft,
  editing: MemberListRow | null,
  emails: number,
): number {
  if (draft.mcpEnabled !== true || !assistantAllowed(draft.scope)) return 0
  if (editing) return editing.mcpEnabled ? 0 : 1
  return emails
}

/**
 * Whether the submit would grant more assistant seats than the plan has left — v3 `2d9ed3d`'s
 * `isMcpLimitExceeded`, extended to a bulk invite (v3 counted one seat however many addresses).
 * `limit: null` is "no ceiling" and never refuses; the backend's own refusal stays authoritative.
 */
export function exceedsAssistantSeats(
  draft: MemberGrantDraft,
  seats: AssistantSeats | null | undefined,
  options: { editing: MemberListRow | null; emailCount: number },
): boolean {
  if (!seats || seats.limit === null) return false
  const needed = seatsNeeded(draft, options.editing, options.emailCount)
  return needed > 0 && seats.used + needed > seats.limit
}

/* ── Validation — keys, never sentences (§13) ────────────────────────────────── */

/** `exceedsAssistantSeats` as the form's validation key, or `null` when the seats suffice. */
export function assistantSeatsKey(
  draft: MemberGrantDraft,
  seats: AssistantSeats | null | undefined,
  editing: MemberListRow | null,
  emailCount: number,
): string | null {
  return exceedsAssistantSeats(draft, seats, { editing, emailCount })
    ? 'workspaceSettings.permissions.invite.validation.assistantSeats'
    : null
}

const VALIDATION = 'workspaceSettings.permissions.invite.validation'

/**
 * The first blocking problem, as an i18n KEY, or `null` when the draft may be submitted.
 *
 * FOUR of v3's five swal literals survive. The fifth — "Please select Permission." (`:459`) — is
 * gone because `MemberGrantDraft.scope` is non-nullable: the state it reported is unrepresentable,
 * and translating a message about an impossible state would just be dead copy in another form.
 */
export function draftValidationKey(
  draft: MemberGrantDraft,
  options: { sendableCount: number; requireEmails: boolean },
): string | null {
  if (draft.role === null) return `${VALIDATION}.role`
  if (options.requireEmails && options.sendableCount === 0) return `${VALIDATION}.email`
  if (draft.scope === 'workspace' && draft.workspaceIds.length === 0) {
    return `${VALIDATION}.workspace`
  }
  if (draft.scope === 'dashboard' && draft.dashboardIds.length === 0) {
    return `${VALIDATION}.dashboard`
  }
  return null
}

/* ── Payloads ────────────────────────────────────────────────────────────────── */

function numericIds(ids: readonly (number | string)[]): number[] {
  return ids.map((id) => Number(id)).filter((id) => Number.isFinite(id))
}

/** The `permission_type` + `entity_ids` pair for a draft (v3 `:484-492`). */
function scopePayload(draft: MemberGrantDraft): MemberGrantPayload {
  const permission = draft.role ?? 'viewer'
  if (draft.scope === 'workspace') {
    return {
      permission,
      permission_type: 'workspace',
      entity_ids: numericIds(draft.workspaceIds),
    }
  }
  if (draft.scope === 'dashboard') {
    return {
      permission,
      permission_type: 'dashboard',
      entity_ids: numericIds(draft.dashboardIds),
    }
  }
  // Account scope carries no entities — v3 omitted the key entirely (`:491`).
  return { permission, permission_type: 'account' }
}

/** The grant body. `mcp_enabled` rides along only when the form offered the switch; a dashboard
 *  grant sends `false`, so moving an assistant holder onto a dashboard grant takes it away rather
 *  than earning the backend's refusal. */
export function grantPayload(draft: MemberGrantDraft): MemberGrantPayload {
  const payload = scopePayload(draft)
  if (draft.mcpEnabled === null) return payload
  return { ...payload, mcp_enabled: draft.mcpEnabled && assistantAllowed(draft.scope) }
}

export function bulkInvitePayload(
  draft: MemberGrantDraft,
  emails: readonly string[],
): BulkInvitePayload {
  return { emails: [...emails], ...grantPayload(draft) }
}

/* ── D9 — the rule v3 computed and then never used ───────────────────────────── */

function sameIds(a: readonly (number | string)[], b: readonly (number | string)[]): boolean {
  const fold = (ids: readonly (number | string)[]): string => ids.map(String).sort().join(',')
  return fold(a) === fold(b)
}

/**
 * Whether an edit would POST exactly what is already stored. Wired to the Update button, which is
 * the whole point: v3 computed this and referenced it nowhere (grep-verified), so a no-op update
 * still round-tripped and still toasted "Update user role was successful".
 */
export function isGrantUnchanged(draft: MemberGrantDraft, row: MemberListRow): boolean {
  return (
    draft.role === row.role &&
    draft.scope === row.scope &&
    sameIds(
      draft.workspaceIds,
      row.workspaces.map((workspace) => workspace.id),
    ) &&
    sameIds(
      draft.dashboardIds,
      row.dashboards.map((dashboard) => dashboard.id),
    ) &&
    (draft.mcpEnabled === null || draft.mcpEnabled === row.mcpEnabled)
  )
}

/** An invite row and a member row take different update bodies; the modal must not re-derive this. */
export const isInviteTarget = isPendingInviteRow
